import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import gsap from 'gsap';
import { useGameStore } from '../../core/store';
import { play, playTypeKey, stopPopupTip } from '../audio';
import { LockerObjectiveTip } from './LockerObjectiveTip';
import './locker.css';

function playFieldKey(e: KeyboardEvent<HTMLInputElement>) {
  if (e.key === 'Backspace' || e.key === 'Delete') {
    playTypeKey({ backspace: true });
    return;
  }
  if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    playTypeKey();
  }
}

export function LockerIdentityUI() {
  const root = useRef<HTMLDivElement>(null);
  const caption = useRef<HTMLParagraphElement>(null);
  const beat = useGameStore((s) => s.lockerBeat);
  const profile = useGameStore((s) => s.profile);
  const setName = useGameStore((s) => s.setName);
  const setNumber = useGameStore((s) => s.setNumber);
  const confirmLockerIdentity = useGameStore((s) => s.confirmLockerIdentity);
  const [printFlash, setPrintFlash] = useState(false);
  const [showTip, setShowTip] = useState(false);

  useEffect(() => {
    if (beat !== 'identity' || !root.current) return;
    setShowTip(false);
    stopPopupTip();

    const tl = gsap.timeline();
    tl.fromTo(
      root.current,
      { x: '110%', opacity: 0 },
      { x: '0%', opacity: 1, duration: 0.9, ease: 'power3.out' },
    );
    if (caption.current) {
      tl.fromTo(
        caption.current,
        { opacity: 0, y: 8 },
        { opacity: 1, y: 0, duration: 1.0, ease: 'power2.out' },
        0.2,
      );
    }
    tl.add(() => setShowTip(true), 0);

    return () => {
      tl.kill();
      stopPopupTip();
    };
  }, [beat]);

  useEffect(() => {
    if (beat !== 'identity') return;
    setPrintFlash(true);
    const id = window.setTimeout(() => setPrintFlash(false), 280);
    return () => window.clearTimeout(id);
  }, [profile.name, profile.number, beat]);

  if (beat !== 'identity' && beat !== 'closing') return null;
  if (beat === 'closing') return null;

  const onNameChange = (value: string) => {
    setName(value);
    if (value.length > 0 && showTip) {
      setShowTip(false);
      stopPopupTip();
    }
  };

  const dismissTip = () => {
    if (!showTip) return;
    setShowTip(false);
    stopPopupTip();
  };

  const onConfirm = () => {
    void play('silence', { gain: 0.5 });
    stopPopupTip();
    setShowTip(false);
    if (root.current) {
      gsap.to(root.current, {
        x: '110%',
        opacity: 0,
        duration: 0.55,
        ease: 'power2.in',
        onComplete: () => confirmLockerIdentity(),
      });
    } else {
      confirmLockerIdentity();
    }
  };

  return (
    <>
      <p className="locker-caption" ref={caption}>
        Every journey begins
        <br />
        before entering the arena.
      </p>

      <LockerObjectiveTip visible={showTip} />

      <aside
        className={`locker-panel${printFlash ? ' is-printing' : ''}`}
        ref={root}
        aria-label="Define your jersey"
        onPointerDown={dismissTip}
      >
        <p className="locker-panel__eyebrow">Identity</p>
        <h2 className="locker-panel__title">Define your jersey.</h2>
        <p className="locker-panel__sub">
          Every legend starts with an identity.
        </p>

        <form
          className="locker-panel__form"
          onSubmit={(e) => {
            e.preventDefault();
            onConfirm();
          }}
        >
          <label className="locker-field">
            <span>Player Name</span>
            <input
              value={profile.name}
              maxLength={12}
              autoComplete="off"
              spellCheck={false}
              autoFocus
              onKeyDown={playFieldKey}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder="PLAYER"
            />
          </label>

          <label className="locker-field">
            <span>Squad Number</span>
            <input
              type="number"
              min={1}
              max={99}
              value={profile.number}
              onKeyDown={playFieldKey}
              onChange={(e) => setNumber(Number(e.target.value))}
            />
          </label>

          <button type="button" className="locker-cta" onClick={onConfirm}>
            Step into the tunnel
          </button>
        </form>
      </aside>
    </>
  );
}
